import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Eye } from 'lucide-react';
import { CustomSelect } from '../../components/CustomSelect';
import { loadBillTemplates } from '../../data/billTemplateStore';
import { effectiveBranchesOf, updateBranch } from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { PrintBillModal } from '../../components/PrintBillModal';
import { SAMPLE_BILL } from '../../utils/receiptPreview';
import {
  BILL_PRESETS,
  DEFAULT_TEMPLATE_ID,
  resolveTemplateSettings,
} from '../../data/billTemplates';

/**
 * Which branch prints with which design.
 *
 * Two designs came before this one and both were wrong in the same way. The
 * first was a bare `<select>`: a name says nothing about what comes out of the
 * printer, so a shop picked one blind and had to print a bill to find out what
 * they had chosen. The second answered that with a thumbnail of the receipt on
 * every row — but a receipt shrunk to fit a card is unreadable, it was drawn
 * twice for every design, and it still did not let anyone actually read the
 * thing.
 *
 * So: a name to choose by, and one button to see the design full size. The
 * picture is drawn once, on demand, at a size that can be read.
 */

export const BranchDesignsTab = ({ org, showNotification }) => {
  const { version } = useSharedStore();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // { name, cfg }

  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, version]);

  useEffect(() => {
    let cancelled = false;
    // A failed load is not fatal here: the built-in presets need no fetch, and
    // one of them is enough to assign.
    loadBillTemplates()
      .then((list) => { if (!cancelled) setTemplates(list); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const settingsFor = (id) => resolveTemplateSettings(id, templates);

  /**
   * The choices, labelled with their paper size.
   *
   * No longer split into ready-made and the shop's own: the starter designs are
   * rows now, so there is one kind. Built once rather than per row — the list is
   * the same for every branch, and rebuilding it inside the map made each
   * dropdown re-render on any unrelated state change.
   */
  const designOptions = useMemo(() => templates.map((t) => ({
    value: t.id,
    label: t.name,
    badge: `${resolveTemplateSettings(t.id, templates).paperWidth} mm`,
  })), [templates]);
  // The presets are still consulted here. A branch chosen before the starter
  // designs became rows holds a `preset:` id until it is reassigned, and its
  // bill must keep printing in the meantime.
  const nameFor = (id) =>
    templates.find((t) => t.id === id)?.name
    || BILL_PRESETS.find((p) => p.id === id)?.name
    || 'Standard Receipt';

  const assign = (branch, templateId) => {
    updateBranch(branch.id, { billTemplateId: templateId });
    showNotification?.(`${branch.name} will print with ${nameFor(templateId)}.`, 'success');
  };

  return (
    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-[#0d0d0d]">Branch bill designs</h3>
          <p className="text-[11px] text-slate-500 mt-1">
            Choose the design each branch prints with. Press the eye to see one full size.
            New designs are made in the <strong>Bill Format</strong> tab.
          </p>
        </div>
        <span className="text-[11px] font-semibold text-slate-400 shrink-0">
          {branches.length} {branches.length === 1 ? 'branch' : 'branches'}
        </span>
      </div>

      {loading ? (
        <p className="text-[11px] text-slate-400">Loading…</p>
      ) : branches.length === 0 ? (
        <p className="text-[11px] text-slate-400">No branches yet.</p>
      ) : (
        <div className="rounded-xl border border-[var(--theme-tertiary)] overflow-hidden">
          {/* Column headings, so the dropdown and the size are labelled once
              rather than repeated as helper text on every row. */}
          {/* No Paper column. The paper belongs to the design, not to the
              branch — a column of its own implied it was set here, and it says
              the same thing the design's own name and preview already do. */}
          <div className="hidden sm:grid grid-cols-[1fr_minmax(0,18rem)_4.5rem] gap-3 items-center px-4 py-2.5 bg-slate-50 border-b border-slate-100">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Branch</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Bill design</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 text-center">
              Actions
            </span>
          </div>

          <div className="divide-y divide-slate-100">
            {branches.map((b) => {
              const currentId = b.billTemplateId || DEFAULT_TEMPLATE_ID;
              const cfg = settingsFor(currentId);
              return (
                <div
                  key={b.id}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_minmax(0,18rem)_4.5rem] gap-2 sm:gap-3 items-center px-4 py-3 bg-white hover:bg-slate-50/60 transition-colors"
                >
                  <span className="text-[13px] font-bold text-[#0d0d0d] truncate">{b.name}</span>

                  {/* The app's own dropdown, not the browser's: a native select
                      cannot show the paper size beside each name, and rendered
                      as a grey system control in the middle of a themed table. */}
                  <CustomSelect
                    value={currentId}
                    onChange={(id) => assign(b, id)}
                    options={designOptions}
                    showBadgeInButton={false}
                    className="w-full normal-case text-xs font-semibold text-slate-700"
                  />

                  <button
                    type="button"
                    onClick={() => setViewing({ name: nameFor(currentId), cfg })}
                    className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-[var(--theme-primary)] hover:bg-[var(--theme-secondary)] transition-colors justify-self-start sm:justify-self-center"
                    aria-label={`Preview ${nameFor(currentId)}`}
                    title="View this design"
                  >
                    <Eye size={15} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewing && (
        <PrintBillModal
          org={org}
          bill={SAMPLE_BILL}
          settings={viewing.cfg}
          title={viewing.name}
          onClose={() => setViewing(null)}
        />
      )}
    </motion.div>
  );
};

BranchDesignsTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
